import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAccount } from "@/queries/account";
import { useAuth } from "@/contexts/AuthContext";
import { Download, Info, Mail, Megaphone } from "lucide-react";
import { shouldShowManualInstallGuide } from "@/lib/pwaInstall";
import { BUILD_META } from "@/buildMeta.generated";

const INSTAGRAM_URL = process.env.REACT_APP_INSTAGRAM_URL || "https://www.instagram.com/lapstr_official/";
const FACEBOOK_URL = process.env.REACT_APP_FACEBOOK_URL || "https://www.facebook.com/profile.php?id=61580617215229";

function InstagramIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5a4.25 4.25 0 0 0 4.25 4.25h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5a4.25 4.25 0 0 0-4.25-4.25h-8.5Zm8.75 2.25a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.5A3.5 3.5 0 1 0 12 15.5 3.5 3.5 0 0 0 12 8.5Z" />
    </svg>
  );
}

function FacebookIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M13.25 22v-8.22h2.77l.42-3.22h-3.19V8.5c0-.93.26-1.56 1.59-1.56h1.7V4.06c-.3-.04-1.3-.12-2.48-.12-2.45 0-4.12 1.5-4.12 4.25v2.37H7.16v3.22h2.78V22h3.31Z" />
    </svg>
  );
}

function formatBuildDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
  }).format(date);
}

export default function AboutPanel() {
  const supportCardRef = React.useRef(null);
  const location = useLocation();
  const [supportCardHighlighted, setSupportCardHighlighted] = React.useState(false);
  const { data: account } = useAccount();
  const { user } = useAuth();
  const role = account?.role || user?.role;
  const isAdmin = role === "super_admin" || role === "club_admin";
  const runtimeEnvironment = process.env.NODE_ENV || "development";
  const environment = runtimeEnvironment || BUILD_META.environment || "development";
  const version = BUILD_META.version || "0.0.0";
  const stage =
    environment === "development"
      ? "dev"
      : environment === "test"
        ? "test"
        : (BUILD_META.stage || "production");
  const buildHash = BUILD_META.buildHash || "";
  const buildDate = BUILD_META.buildDate || "";
  const formattedBuildDate = formatBuildDate(buildDate);
  const showInstallPrompt = shouldShowManualInstallGuide();

  const openInstallPrompt = () => {
    try {
      window.dispatchEvent(new Event("lapstr:open-install-modal"));
    } catch { }
  };

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.hash !== "#support-card") return;

    const node = supportCardRef.current;
    if (!node) return;

    const scrollTimer = window.setTimeout(() => {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      setSupportCardHighlighted(true);
      if (typeof node.focus === "function") {
        node.focus({ preventScroll: true });
      }
    }, 80);

    const clearTimer = window.setTimeout(() => {
      setSupportCardHighlighted(false);
    }, 2600);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
  }, [location.hash]);

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 shrink-0 opacity-70" />
            <span>Om appen</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">Version: {version}</Badge>
            {buildHash ? <Badge variant="outline">Build: {buildHash}</Badge> : null}
            {formattedBuildDate ? <Badge variant="outline">Byggd: {formattedBuildDate}</Badge> : null}
            {isAdmin ? <Badge variant="outline">Miljö: {environment}</Badge> : null}
            {isAdmin ? <Badge>{stage}</Badge> : null}
          </div>

          <p>Lapstr är en mobilanpassad tjänst där du enkelt för digitala matchprotokoll och följer resultaten i Bauhausligan i speedway.</p>

          <div
            id="support-card"
            ref={supportCardRef}
            tabIndex={-1}
            className={[
              "rounded-xl border border-border/60 bg-muted/30 p-4 outline-none transition-all duration-500",
              supportCardHighlighted
                ? "ring-2 ring-amber-400/80 ring-offset-2 ring-offset-background shadow-[0_0_0_1px_rgba(251,191,36,0.25),0_0_28px_rgba(251,191,36,0.2)]"
                : "",
            ].join(" ")}
          >
            <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
              <Mail className="h-4 w-4 opacity-70" />
              <span>Support</span>
            </div>
            <p className="text-muted-foreground">
              För kontakt, annonser, feedback eller buggar:{" "}
              <a className="text-primary underline-offset-4 hover:underline" href="mailto:support@lapstr.se">
                support@lapstr.se
              </a>
              .
            </p>
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
              <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
                <Megaphone className="h-4 w-4 opacity-70" />
                <span>Följ Lapstr</span>
              </div>
            <p className="mb-3 text-muted-foreground">
              Följ Lapstr på sociala medier för uppdateringar, nyheter och driftinfo.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="icon" className="h-10 w-10 rounded-full">
                <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer">
                  <InstagramIcon className="h-5 w-5" />
                  <span className="sr-only">Instagram</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="icon" className="h-10 w-10 rounded-full">
                <a href={FACEBOOK_URL} target="_blank" rel="noreferrer">
                  <FacebookIcon className="h-5 w-5" />
                  <span className="sr-only">Facebook</span>
                </a>
              </Button>
            </div>
          </div>

          {showInstallPrompt ? (
            <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
              <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
                <Download className="h-4 w-4 opacity-70" />
                <span>Installera appen</span>
              </div>
              <p className="mb-3 text-muted-foreground">
                Installera Lapstr som en webbapp på din mobil, surfplatta eller dator.
              </p>
              <Button variant="outline" size="sm" onClick={openInstallPrompt}>
                Visa installationsguide
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {isAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle>Admin</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            <p>Gå till adminpanelen för att hantera matcher, lineups och annonser.</p>
            <div>
              <Button asChild variant="outline" size="sm">
                <Link to="/admin">Till admin</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
